export type BackendFieldError = {
  field?: string;
  name?: string;
  message?: string;
};

export type BackendErrorLike = {
  response?: { data?: unknown };
  data?: unknown;
} & Record<string, unknown>;

export function extractFieldErrors(
  err: unknown,
): Record<string, string> | null {
  if (!err || typeof err !== "object") return null;

  const wrapped = err as BackendErrorLike;
  const data =
    (wrapped.response?.data as Record<string, unknown> | undefined) ??
    (wrapped.data as Record<string, unknown> | undefined) ??
    (err as Record<string, unknown>);

  if (
    data &&
    typeof data === "object" &&
    data.errors &&
    typeof data.errors === "object" &&
    !Array.isArray(data.errors)
  ) {
    const errors = data.errors as Record<string, unknown>;
    const mapped: Record<string, string> = {};
    for (const [name, message] of Object.entries(errors)) {
      mapped[name] = String(message);
    }
    return Object.keys(mapped).length > 0 ? mapped : null;
  }

  if (data && Array.isArray((data as { field_errors?: unknown }).field_errors)) {
    const arr = (data as { field_errors: BackendFieldError[] }).field_errors;
    const mapped: Record<string, string> = {};
    for (const entry of arr) {
      const name = entry?.field ?? entry?.name;
      if (name) {
        mapped[name] = String(entry?.message ?? "Invalid value");
      }
    }
    return Object.keys(mapped).length > 0 ? mapped : null;
  }

  return null;
}

export function readErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== "object") return fallback;
  const wrapped = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    wrapped.response?.data?.message ??
    wrapped.response?.data?.error ??
    wrapped.message ??
    fallback
  );
}

export function splitOffStepErrors(
  fieldErrors: Record<string, string>,
  stepFieldNames: string[],
): { onStep: Record<string, string>; offStep: string[] } {
  const names = new Set(stepFieldNames);
  const onStep: Record<string, string> = {};
  const offStep: string[] = [];
  for (const [name, message] of Object.entries(fieldErrors)) {
    if (names.has(name)) {
      onStep[name] = message;
    } else {
      offStep.push(message);
    }
  }
  return { onStep, offStep };
}
